import Counter from '@/components/counter'
import { motion, useInView } from 'framer-motion'
import { ReactNode, useEffect, useRef, useState } from 'react'

type StatProps = {
  value: number
  fraction?: string
  valueUnit?: string
  stat: string | ReactNode
  graphic: ReactNode
  counterDuration: number
  counterDelay: number
}

export default function Stat({ value, fraction, valueUnit, stat, graphic, counterDuration, counterDelay }: StatProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isVisible = useInView(ref, { once: true })
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => {
        setIsActive(true)
      }, counterDelay * 1000)
    }
  }, [isVisible])

  return (
    <div ref={ref} className="flex flex-col items-center justify-center mx-auto md:flex-row md:items-end">
      <div className="grow shrink-0 basis-1/2 w-full mb-1 md:mb-0 md:max-w-[300px] [&_svg]:ml-auto md:[&_svg]:max-h-36">
        {graphic}
      </div>
      <motion.div
        className="flex items-center md:items-start md:flex-col md:ml-4"
        animate={isVisible ? 'show' : 'hide'}
        transition={{
          duration: 0.75,
          ease: 'anticipate',
          staggerChildren: 0.1,
        }}
      >
        <motion.h3
          className="text-5xl md:text-6xl xl:text-8xl text-primaryBlue dark:text-secondary-400 mb-0"
          initial={{ opacity: 0, translateY: 10 }}
          variants={{
            hide: {
              opacity: 0,
              translateY: 10,
            },
            show: {
              opacity: 1,
              translateY: 0,
            },
          }}
        >
          <Counter
            duration={counterDuration}
            from={0}
            to={value}
            postfix={
              <>
                {fraction && <span>/{fraction}</span>}
                {valueUnit && <span className="text-5xl md:text-3xl xl:text-5xl">{valueUnit}</span>}
              </>
            }
            isActive={isActive}
          />
        </motion.h3>
        <motion.p
          className="text-base ml-5 md:text-xl md:ml-0 md:max-w-[275px]) leading-tight mb-0"
          initial={{ opacity: 0, translateY: 10 }}
          variants={{
            hide: {
              opacity: 0,
              translateY: 10,
            },
            show: {
              opacity: 1,
              translateY: 0,
            },
          }}
        >
          {stat}
        </motion.p>
      </motion.div>
    </div>
  )
}
